import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';

import './styles.css';
import { Boleto } from './interfaces';
import { formatNumberToReais } from '../../../utils/MoneyFormat';
import { useAccessibility } from '../../../Store/contexts/AccessibilityContext';
import DefaultButton from '../../../components/Accessibility/DefaultButton';
import LoadingApp from '../../../components/LoadingApp';
import ModalFeedBack from '../../../components/ModalFeedBack';
import { 
  sxTable
} from './sxProps';

interface TabelaMensalidadesProps {
  mensalidades: Boleto[];
  isPessoaFisica: boolean;
  codigoEmpresa?: string;
}

const TabelaMensalidades: React.FC<TabelaMensalidadesProps> = ({ mensalidades, isPessoaFisica, codigoEmpresa }) => {
  const [loadingBoleto, setLoadingBoleto] = useState<boolean>(false);
  const [mensalidadesState, setMensalidadesState] = useState<Boleto[]>(mensalidades);
  const [errorModal, setErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { textStyleAccessibility, contrastStyle, contrastBorderAccessibility, contrastColorAccessibility } = useAccessibility();

  const checkboxRefs = useRef<(HTMLInputElement | null)[]>([]);
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontFamily: 'Unimed-Sans',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    ...textStyleAccessibility
  }));
  
  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: '#F9F9F9'
    },
  }));

  const calcularValorTotal = (): number => {
    return mensalidadesState
      .filter(m => m.selecionada)
      .reduce((acc, m) => acc + m.valorCorrigido, 0);
  };

  const getDataAtual = (): string => {
    return dayjs().format('DD/MM/YYYY');
  };

  const handleCheckboxChange = (index: number, shouldFocus: boolean = true) => {
    const novaMensalidades = [...mensalidadesState];
    const mensalidadeClicada = novaMensalidades[index];

    if (mensalidadeClicada.desabilitada) {
      return;
    }

    if (!mensalidadeClicada.selecionada) {
      mensalidadeClicada.selecionada = true;

      for (let i = 0; i < index; i++) {
        novaMensalidades[i].selecionada = true;
      }
    } else {
      mensalidadeClicada.selecionada = false;

      for (let i = index + 1; i < novaMensalidades.length; i++) {
        novaMensalidades[i].selecionada = false;
      }
    }

    setMensalidadesState(novaMensalidades);
    if (shouldFocus) {
      setTimeout(() => {
        const nextEnabledIndex = novaMensalidades.findIndex((m, i) => i > index && !m.desabilitada);
        if (nextEnabledIndex !== -1) {
          checkboxRefs.current[nextEnabledIndex]?.focus();
        }
      }, 0);
    }
  };

  const handleGerarBoleto = async () => {
    const mensalidadesSelecionadas = mensalidadesState.filter(m => m.selecionada);

    if (mensalidadesSelecionadas.length === 0) {
      setErrorMessage('Selecione ao menos uma mensalidade');
      setErrorModal(true);
      return;
    }

    setLoadingBoleto(true);

    try {
      const payload = {
        mensalidades: mensalidadesSelecionadas.map(m => ({
          id: m.id,
          vencimento: m.vencimento,
          valorCorrigido: m.valorCorrigido,
        })),
        agrupado: mensalidadesSelecionadas.length > 1,
        dataPagamento: getDataAtual(),
        valorTotal: calcularValorTotal(),
      };

      console.log('POST /api/gerar-boleto', payload);
      await new Promise(resolve => setTimeout(resolve, 1500));
      alert('Boleto gerado com sucesso! (simulação)');
      
    } catch (error: any) {
      setErrorMessage(error?.message || 'Erro ao gerar boleto');
      setErrorModal(true);
    } finally {
      setLoadingBoleto(false);
    }
  };

  useEffect(() => {
    setMensalidadesState(mensalidades);
  }, [mensalidades]);

  const handleCloseErrorModal = () => {
    setErrorModal(false);
  };

  const getRowAriaLabel = (mensalidade: Boleto) => {
    const estadoSelecao = mensalidade.selecionada ? 'Selecionado' : 'Não selecionado';
    return `${estadoSelecao}, Vencimento ${mensalidade.vencimento}, Valor ${formatNumberToReais(mensalidade.valor)}, ${mensalidade.diasAtraso} dias em atraso, Juros ${mensalidade.juros}, Multa ${formatNumberToReais(mensalidade.multa)}, Valor corrigido ${formatNumberToReais(mensalidade.valorCorrigido)}`;
  };

  const handleTableWrapperFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    if (e.target === tableWrapperRef.current) {
      setTimeout(() => {
        const firstEnabledIndex = mensalidadesState.findIndex(m => !m.desabilitada);
        if (firstEnabledIndex !== -1) {
          checkboxRefs.current[firstEnabledIndex]?.focus();
        }
      }, 0);
    }
  };

  return (
    <div className='container-tabela-mensalidades'>
      <LoadingApp showLoading={loadingBoleto} />

      <ModalFeedBack
        showModal={errorModal}
        handleShowModal={handleCloseErrorModal}
        icon={'error'}
        textButton={'Fechar'}
        title={'Erro'}
        subTitle={errorMessage}
        showButtonModal
        handleButtonModal={handleCloseErrorModal}
        classNameModal="modal-feedback-error"
      />

      <div
        ref={tableWrapperRef}
        tabIndex={0}
        onFocus={handleTableWrapperFocus}
        style={{ outline: 'none' }}
      >
        <TableContainer 
          className='container-table' 
          style={{ ...contrastBorderAccessibility, ...contrastStyle }} 
          sx={sxTable} 
          component={Paper}
        >
          <Table stickyHeader size="small" aria-label="tabela de mensalidades">
            <TableHead>
              <TableRow>
                <StyledTableCell padding="checkbox">
                  <b></b>
                </StyledTableCell>
                <StyledTableCell style={textStyleAccessibility}><b>Vencimento</b></StyledTableCell>
                <StyledTableCell align="right"><b>Valor</b></StyledTableCell>
                <StyledTableCell align="center"><b>Dias em atraso</b></StyledTableCell>
                <StyledTableCell align="center"><b>Juros</b></StyledTableCell>
                <StyledTableCell align="right"><b>Multa</b></StyledTableCell>
                <StyledTableCell align="right"><b>Valor corrigido</b></StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mensalidadesState.map((mensalidade, index) => (
                <StyledTableRow
                  key={mensalidade.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <StyledTableCell padding="checkbox">
                    <Checkbox
                      inputRef={(el) => (checkboxRefs.current[index] = el)}
                      color="primary"
                      style={contrastColorAccessibility}
                      className={mensalidade.desabilitada ? 'checkbox-disabled' : ''}
                      checked={mensalidade.selecionada}
                      onChange={() => handleCheckboxChange(index)}
                      disabled={mensalidade.desabilitada}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          const nextEnabledIndex = mensalidadesState.findIndex((m, i) => i > index && !m.desabilitada);
                          if (nextEnabledIndex !== -1) {
                            checkboxRefs.current[nextEnabledIndex]?.focus();
                          }
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          const prevEnabledIndex = mensalidadesState.slice(0, index).reverse().findIndex(m => !m.desabilitada);
                          if (prevEnabledIndex !== -1) {
                            const originalIndex = index - 1 - prevEnabledIndex;
                            checkboxRefs.current[originalIndex]?.focus();
                          }
                        }
                      }}
                      inputProps={{
                        'aria-label': getRowAriaLabel(mensalidade),
                        'aria-checked': mensalidade.selecionada,
                        tabIndex: -1
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row">
                    {mensalidade.vencimento}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatNumberToReais(mensalidade.valor)}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {mensalidade.diasAtraso}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {mensalidade.juros}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatNumberToReais(mensalidade.multa)}
                  </StyledTableCell>
                  <StyledTableCell align="right" sx={{ fontFamily: 'UnimedSans-Bold', fontWeight: 700 }}>
                    {formatNumberToReais(mensalidade.valorCorrigido)}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div className="data-e-total-container">
        <div className="input-data-container">
          <Typography className="input-data-label" style={textStyleAccessibility}>
            Data de pagamento do boleto <span className='input-data-label-span'>(dd/mm/aaaa)</span>
          </Typography>
          <Typography className="input-data-value" style={textStyleAccessibility}>
            {getDataAtual()}
          </Typography>
        </div>

        <div className="valor-total-container">
          <Typography className="valor-total-label" style={textStyleAccessibility}>
            Valor total das mensalidades selecionadas:
          </Typography>
          <Typography className="valor-total-value" style={textStyleAccessibility}>
            {formatNumberToReais(calcularValorTotal())}
          </Typography>
        </div>
      </div>

      <DefaultButton
        text="GERAR BOLETO"
        onClick={handleGerarBoleto}
        disabled={loadingBoleto}
        style={{ maxWidth: '250px' }}
      />
    </div>
  );
};

export default TabelaMensalidades;